// @flow
import * as React from 'react';
import { Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useClient } from '../../hooks/client-context';
import { getToken } from '@coverright/shared/keycloak';
import { environment } from '../../../../../environments/environment';
import { enqueueSnackbar } from 'notistack';
import { NoData, Portal, useAddClientFilesMutation, useDeleteClientFileMutation } from '../../../../shared';
import { Download, UploadSimple, X } from '@phosphor-icons/react';
import { Client } from '../../../../shared/api/use-client-subscription';
import { useState } from 'react';

export const Files = () => {
  const client = useClient();
  return (
    <Stack gap={3}>
      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography variant={'subtitle1'} flex={1}>Files</Typography>
        <UploadButton />
      </Stack>

      {!client.clientFiles?.length && <NoData height={80}>No files</NoData>}

      {client.clientFiles?.map(file => (
        <Portal key={file.link} p={1}>
          <Stack gap={2} direction={'row'} alignItems={'center'}>
            <Typography variant={'body2'} flex={1}>
              {file.name}
            </Typography>

            <DownloadButton file={file} />
            <DeleteButton file={file} />
          </Stack>
        </Portal>
      ))}
    </Stack>
  );
};

const UploadButton = () => {
  const client = useClient();
  const [addClientFiles] = useAddClientFilesMutation();
  const [loading, setLoading] = useState(false);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (files?.length) {
      setLoading(true)

      try {
        const formData = new FormData();
        Array.from(files).forEach(file => formData.append('files', file, file.name))
        formData.append('clientId', client.id)

        const token = await getToken()

        const requestOptions = {
          method: 'POST',
          headers: {
            authorization: 'Bearer ' + token
          },
          body: formData
        };

        const url = environment.clientFileUploadUrl + '/api/client-file'

        await fetch(url, requestOptions)
          .then(res => res.json())
          .then(res => addClientFiles({variables: {
              clientId: client?.id,
              clientFiles: res.links
            }}))
          .catch(() => enqueueSnackbar('Error in uploading file!', {variant: "error"}))
      } finally {
        setLoading(false);
      }
    }
  }

  return <>
    {loading && <CircularProgress size={20} />}
    <div>
      <input onChange={onChange} multiple
             style={{ display: 'none' }} id={'file-upload'} type="file" />
      <label htmlFor={'file-upload'}>
        <Button variant={'outlined'} startIcon={<UploadSimple />} component="span" size={'small'}>
          Upload
        </Button>
      </label>
    </div>
  </>
}

const DeleteButton = ({ file }: { file: Client['clientFiles'][0] }) => {
  const client = useClient();
  const [loading, setLoading] = useState(false);
  const [deleteFile] = useDeleteClientFileMutation();

  const handleClick = async () => {
    setLoading(true);

    try {
      const token = await getToken();
      const requestOptions = {
        method: 'DELETE',
        headers: {
          authorization: 'Bearer ' + token
        },
      };
      const url =  environment.clientFileUploadUrl + file.link;
      await Promise.all([
        fetch(url, requestOptions),
        deleteFile({variables: {clientId: client.id, link: file.link}})
      ])
    } finally {
      setLoading(false);
    }

  }

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return <IconButton size={'small'} onClick={handleClick}>
    <X color={'#DE3124'}/>
  </IconButton>
}

const DownloadButton = ({file}: {file: Client['clientFiles'][0]}) => {
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);
    const token = await getToken()

    const requestOptions = {
      method: 'GET',
      headers: {
        authorization: 'Bearer ' + token
      },
    };
    const url = environment.clientFileUploadUrl + file.link;

    try {
      const response = await fetch(url, requestOptions);
      const blob = await response.blob();
      if (blob) {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return <IconButton size={'small'} onClick={download}>
    <Download />
  </IconButton>
}
